import type { TextMessagingProvider } from '@kizen/api/text-messaging-provider';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithRequired } from './types';

import { getTextMessagingProviders } from '@kizen/api/text-messaging-provider';
import { getResource } from './resource';
import { curry1 } from './utils';

type TextMessagingProvidersQuery<TData = TextMessagingProvider[]> =
  WithRequired<
    QueryObserverOptions<
      TextMessagingProvider[],
      DefaultError,
      TData,
      TextMessagingProvider[],
      [Resource<Readonly<['text-messaging-provider']>>]
    >,
    'queryFn' | 'queryKey'
  >;

export const textMessagingProvidersQuery = <TData = TextMessagingProvider[]>(
  axios: AxiosInstance,
  options?: Omit<TextMessagingProvidersQuery<TData>, 'queryFn' | 'queryKey'>
): TextMessagingProvidersQuery<TData> => {
  const path = ['text-messaging-provider'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;

      return getTextMessagingProviders(axios, { signal });
    },
    ...options,
  };
};

class Queries {
  public textMessagingProvidersQuery: <TData = TextMessagingProvider[]>(
    ...args: Drop<Parameters<typeof textMessagingProvidersQuery<TData>>>
  ) => TextMessagingProvidersQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.textMessagingProvidersQuery = curry1(textMessagingProvidersQuery)(
      axios
    );
  }
}

export type TextMessagingProviderQueries = Queries;

export const defTextMessagingProviderQueries = (
  axios: AxiosInstance
): TextMessagingProviderQueries => {
  return new Queries(axios);
};
